import React, { useState, useEffect } from 'react';
import { ChevronDownRegular, PersonRegular, SearchRegular, CheckmarkRegular } from '@fluentui/react-icons';
import { useLanguage } from '../contexts/LanguageContext';
import { useTextSize } from '../contexts/TextSizeContext';
import propertyService from '../services/propertyService';
import joinPersonUnitService from '../services/joinPersonUnitService';

/**
 * ContactComposeView - Contact selection section for composing emails
 * Allows selecting properties and filtering contacts by roles (Beiräte, Eigentümer, Anwohner)
 */
export default function ContactComposeView({ isOpen, onToggle }) {
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();

  // Properties state
  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertySearchQuery, setPropertySearchQuery] = useState('');
  const [showPropertyList, setShowPropertyList] = useState(false);

  // Contacts state
  const [allContacts, setAllContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Recipient type selection modal
  const [showRecipientTypeModal, setShowRecipientTypeModal] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);

  // Debounced property search - only fetch after user stops typing for 400ms
  useEffect(() => {
    if (!isOpen) return;
    
    // Only search if query is at least 2 characters
    if (propertySearchQuery.length < 2) {
      setProperties([]);
      setShowPropertyList(false);
      return;
    }

    setLoadingProperties(true);
    setShowPropertyList(true);

    // Debounce the search
    const timeoutId = setTimeout(async () => {
      try {
        const response = await propertyService.getAllProperties(
          { name: propertySearchQuery }, 
          { limit: 20 }
        );
        if (response.success && response.data && response.data.data) {
          setProperties(response.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch properties:', err);
      } finally {
        setLoadingProperties(false);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [propertySearchQuery, isOpen]);

  // Reset search when section closes
  useEffect(() => {
    if (!isOpen) {
      setPropertySearchQuery('');
      setProperties([]);
      setShowPropertyList(false);
    }
  }, [isOpen]);

  // Fetch all contacts for selected property
  useEffect(() => {
    const fetchPropertyContacts = async () => {
      if (!selectedProperty) {
        setAllContacts([]);
        return;
      }

      setLoadingContacts(true);
      try {
        const response = await joinPersonUnitService.getAllJoinPersonUnits(
          { 
            property_id: selectedProperty.id,
            active: true,
            enhanced: true
          },
          { limit: 1000 }
        );

        if (response.success && response.data) {
          // Filter out entries without contacts
          const contactsWithDetails = response.data.filter(
            rel => rel.contact_id && rel.contact
          );
          setAllContacts(contactsWithDetails);
        }
      } catch (err) {
        console.error('Failed to fetch property contacts:', err);
        setAllContacts([]);
      } finally {
        setLoadingContacts(false);
      }
    };

    fetchPropertyContacts();
  }, [selectedProperty]);

  // Handle property selection
  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    setPropertySearchQuery(property.name || property.address || property.id);
    setShowPropertyList(false);
    setProperties([]); // Clear the list after selection
  };

  // Filter contacts by role
  const getContactsByRole = (role) => {
    if (!allContacts.length) return [];

    let filteredContacts = [];

    if (role === 'council') {
      // Beiräte: advisory_board === true (regardless of type)
      filteredContacts = allContacts.filter(rel => rel.advisory_board === true);
    } else if (role === 'owners') {
      // Eigentümer: type === "Eigentümer"
      filteredContacts = allContacts.filter(rel => rel.type === 'Eigentümer');
    } else if (role === 'residents') {
      // Anwohner/Mieter: type === "Mieter"
      filteredContacts = allContacts.filter(rel => rel.type === 'Mieter');
    }

    // Extract unique contacts with their emails
    return joinPersonUnitService.extractUniqueContacts(filteredContacts);
  };

  // Handle role button click - show recipient type selection
  const handleRoleClick = (role) => {
    if (!selectedProperty) {
      alert(t('compose.selectProperty'));
      return;
    }

    if (loadingContacts) {
      return; // Don't proceed if still loading
    }

    setPendingRole(role);
    setShowRecipientTypeModal(true);
  };

  // Handle recipient type selection (TO, CC, BCC)
  const handleRecipientTypeSelect = async (recipientType) => {
    setShowRecipientTypeModal(false);

    if (!pendingRole) return;

    try {
      const contacts = getContactsByRole(pendingRole);
      
      if (contacts.length === 0) {
        alert(t('compose.noContactsForRole'));
        return;
      }

      // Extract email addresses
      const emailAddresses = contacts
        .filter(contact => contact.email)
        .map(contact => contact.email);

      if (emailAddresses.length === 0) {
        alert(t('compose.noEmailAddresses'));
        return;
      }

      // Insert emails into the compose field using Office.js
      await addRecipientsToEmail(emailAddresses, recipientType);

      // Success message
      alert(t('compose.recipientsAdded', { 
        count: emailAddresses.length, 
        type: recipientType 
      }));

    } catch (err) {
      console.error('Failed to add recipients:', err);
      alert(t('compose.failedToAddRecipients'));
    } finally {
      setPendingRole(null);
    }
  };

  // Add recipients to email using Office.js
  const addRecipientsToEmail = async (emailAddresses, recipientType) => {
    return new Promise((resolve, reject) => {
      if (typeof Office === 'undefined' || !Office.context || !Office.context.mailbox) {
        reject(new Error('Office.js not available'));
        return;
      }

      const item = Office.context.mailbox.item;
      
      if (!item) {
        reject(new Error('No email item available'));
        return;
      }

      // Convert email addresses to recipients format
      const recipients = emailAddresses.map(email => ({ emailAddress: email }));

      // Add recipients based on type
      if (recipientType === 'to') {
        item.to.addAsync(recipients, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(result.error.message));
          }
        });
      } else if (recipientType === 'cc') {
        item.cc.addAsync(recipients, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(result.error.message));
          }
        });
      } else if (recipientType === 'bcc') {
        item.bcc.addAsync(recipients, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(result.error.message));
          }
        });
      } else {
        reject(new Error('Invalid recipient type'));
      }
    });
  };

  return (
    <div className="border-b border-gray-300">
      <button
        className={`flex items-center justify-between w-full ${currentTextSize.cardPadding} hover:bg-gray-50 transition-colors`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <PersonRegular className="h-4 w-4 text-gray-600" />
          <h3 className={`${currentTextSize.headerText} font-semibold text-gray-900`}>
            {t('compose.contacts')}
          </h3>
        </div>
        <ChevronDownRegular
          className={`h-4 w-4 text-gray-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className={`px-4 pb-4 ${currentTextSize.spacing}`}>
          {/* Explanatory Text */}
          <p className={`${currentTextSize.bodyText} text-gray-600 mb-3`}>
            {t('compose.selectProperty')}
          </p>

          {/* Property Search Input */}
          <div className="relative mb-3">
            <SearchRegular className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={propertySearchQuery}
              onChange={(e) => {
                const newValue = e.target.value;
                setPropertySearchQuery(newValue);
                // Clear selected property when user starts typing again
                if (selectedProperty && newValue !== (selectedProperty.name || selectedProperty.address || selectedProperty.id)) {
                  setSelectedProperty(null);
                }
                if (newValue.length >= 2) {
                  setShowPropertyList(true);
                }
              }}
              onFocus={() => {
                // Only show list on focus if no property is selected
                if (!selectedProperty && propertySearchQuery.length >= 2) {
                  setShowPropertyList(true);
                }
              }}
              placeholder={t('compose.searchPropertyPlaceholder')}
              className={`w-full pl-9 pr-3 py-2 border border-gray-300 rounded ${currentTextSize.bodyText} focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Property Search Results - Only show if no property is selected */}
          {!selectedProperty && showPropertyList && propertySearchQuery.length >= 2 && (
            <div className="mb-4">
              {loadingProperties ? (
                <div className="text-center py-3 border border-gray-200 rounded bg-gray-50">
                  <p className={`${currentTextSize.caption} text-gray-500`}>
                    {t('compose.loadingProperties')}
                  </p>
                </div>
              ) : properties.length === 0 ? (
                <div className="text-center py-3 border border-gray-200 rounded bg-gray-50">
                  <p className={`${currentTextSize.caption} text-gray-500`}>
                    {t('compose.noProperties')}
                  </p>
                </div>
              ) : (
                <div className="border border-gray-300 rounded max-h-60 overflow-y-auto">
                  {properties.map((property) => (
                    <button
                      key={property.id}
                      onClick={() => handlePropertySelect(property)}
                      className={`w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-200 last:border-b-0 transition-colors ${
                        selectedProperty?.id === property.id ? 'bg-blue-100' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`${currentTextSize.bodyText} font-medium text-gray-900`}>
                            {property.name || property.address || property.id}
                          </p>
                          {property.type && (
                            <p className={`${currentTextSize.caption} text-gray-500`}>
                              {property.type}
                            </p>
                          )}
                        </div>
                        {selectedProperty?.id === property.id && (
                          <CheckmarkRegular className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Hint for minimum characters - Only show if no property is selected */}
          {!selectedProperty && propertySearchQuery.length > 0 && propertySearchQuery.length < 2 && (
            <p className={`${currentTextSize.caption} text-gray-400 mb-4`}>
              {t('compose.searchPropertyHint')}
            </p>
          )}

          {/* Role Buttons */}
          {selectedProperty && (
            <>
              {loadingContacts ? (
                <div className="text-center py-4">
                  <p className={`${currentTextSize.bodyText} text-gray-500`}>
                    {t('compose.loadingContacts')}
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex gap-2 mb-4">
                    <button
                      onClick={() => handleRoleClick('council')}
                      className={`flex-1 px-4 py-2 ${currentTextSize.bodyText} font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors`}
                    >
                      {t('compose.councilMembers')}
                    </button>
                    <button
                      onClick={() => handleRoleClick('owners')}
                      className={`flex-1 px-4 py-2 ${currentTextSize.bodyText} font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors`}
                    >
                      {t('compose.owners')}
                    </button>
                    <button
                      onClick={() => handleRoleClick('residents')}
                      className={`flex-1 px-4 py-2 ${currentTextSize.bodyText} font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors`}
                    >
                      {t('compose.residents')}
                    </button>
                  </div>

                  {/* Contact count info */}
                  {allContacts.length > 0 && (
                    <p className={`${currentTextSize.caption} text-gray-500 mb-4`}>
                      {t('compose.contactsFound', { count: allContacts.length })}
                    </p>
                  )}
                </>
              )}

              {/* Divider */}
              <div className="border-t border-gray-300 my-4"></div>

              {/* More Contacts Section (to be filled later) */}
              <div className="text-center py-4">
                <p className={`${currentTextSize.caption} text-gray-400`}>
                  {/* Placeholder for additional contacts */}
                </p>
              </div>
            </>
          )}

          {/* Recipient Type Selection Modal */}
          {showRecipientTypeModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
                <h3 className={`${currentTextSize.headerText} font-semibold text-gray-900 mb-4`}>
                  {t('compose.selectRecipientType')}
                </h3>
                <p className={`${currentTextSize.bodyText} text-gray-600 mb-4`}>
                  {t('compose.selectRecipientTypeDescription')}
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => handleRecipientTypeSelect('to')}
                    className={`w-full px-4 py-3 ${currentTextSize.bodyText} font-medium text-left text-gray-900 bg-gray-100 rounded hover:bg-gray-200 transition-colors`}
                  >
                    <span className="font-semibold">An:</span> {t('compose.recipientTypeTo')}
                  </button>
                  <button
                    onClick={() => handleRecipientTypeSelect('cc')}
                    className={`w-full px-4 py-3 ${currentTextSize.bodyText} font-medium text-left text-gray-900 bg-gray-100 rounded hover:bg-gray-200 transition-colors`}
                  >
                    <span className="font-semibold">CC:</span> {t('compose.recipientTypeCc')}
                  </button>
                  <button
                    onClick={() => handleRecipientTypeSelect('bcc')}
                    className={`w-full px-4 py-3 ${currentTextSize.bodyText} font-medium text-left text-gray-900 bg-gray-100 rounded hover:bg-gray-200 transition-colors`}
                  >
                    <span className="font-semibold">BCC:</span> {t('compose.recipientTypeBcc')}
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowRecipientTypeModal(false);
                    setPendingRole(null);
                  }}
                  className={`w-full mt-4 px-4 py-2 ${currentTextSize.bodyText} font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors`}
                >
                  {t('contactCreator.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

